import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CForm,
  CContainer,
  CRow,
  CCol,
  CFormCheck,
  CButton,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody
} from '@coreui/react';
import PropTypes from 'prop-types';
import axios from 'axios';
import CIcon from '@coreui/icons-react';
import { cilPlus, cilMinus } from '@coreui/icons';

const InvestigationJustificationTab = forwardRef(({ initialData, onSave }, ref) => {
  const [justificationText, setJustificationText] = useState('');
  const [orgName, setOrgName] = useState('');
  const [workTitle, setWorkTitle] = useState('');
  const [workName, setWorkName] = useState('');
  const [workCipher, setWorkCipher] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [techTask, setTechTask] = useState('');
  const [goalsAndTasks, setGoalsAndTasks] = useState([]);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [initialInfo, setInitialInfo] = useState(false);
  const [advancePaymentDate, setAdvancePaymentDate] = useState('');
  const [infoProvidedDate, setInfoProvidedDate] = useState('');

  // Модальное окно для справочника
  const [showModal, setShowModal] = useState(false);
  const [goalsData, setGoalsData] = useState([]);
  const [selectedModalGoalIndex, setSelectedModalGoalIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setJustificationText(initialData.justificationText || '');
      setOrgName(initialData.orgName || '');
      setWorkTitle(initialData.workTitle || '');
      setWorkName(initialData.workName || '');
      setWorkCipher(initialData.workCipher || '');
      setContractDate(initialData.contractDate || '');
      setContractNumber(initialData.contractNumber || '');
      setContractEndDate(initialData.contractEndDate || '');
      setResponsiblePerson(initialData.responsiblePerson || '');
      setTechTask(initialData.techTask || '');
      setGoalsAndTasks(initialData.goalsAndTasks || []);
      setInitialInfo(initialData.initialInfo || false);
      setAdvancePaymentDate(initialData.advancePaymentDate || '');
      setInfoProvidedDate(initialData.infoProvidedDate || '');
    }
  }, [initialData]);

  // Загрузка данных из API
  const fetchGoalsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/typetable/?ntype=51&id_parent=13182&table=typetable', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      const sorted = data.sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));
      setGoalsData(sorted);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setGoalsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedModalGoalIndex(null);
    fetchGoalsData();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedModalGoalIndex(null);
  };

  const handleGoalDoubleClick = (goal) => {
    const newId = Math.max(...goalsAndTasks.map(item => item.id), 0) + 1;
    const newNumber = goalsAndTasks.length + 1;

    const newGoal = {
      id: newId,
      number: newNumber,
      name: goal.Name,
      apiData: goal
    };

    const updatedGoals = [...goalsAndTasks, newGoal];
    setGoalsAndTasks(updatedGoals);
    setSelectedGoalId(newId);
    handleCloseModal();
  };

  const handleGoalClick = (id) => {
    setSelectedGoalId(id);
  };

  const handleJustificationChange = (e) => setJustificationText(e.target.value);
  const handleOrgNameChange = (e) => setOrgName(e.target.value);
  const handleWorkTitleChange = (e) => setWorkTitle(e.target.value);
  const handleWorkNameChange = (e) => setWorkName(e.target.value);
  const handleWorkCipherChange = (e) => setWorkCipher(e.target.value);
  const handleContractDateChange = (e) => setContractDate(e.target.value);
  const handleContractNumberChange = (e) => setContractNumber(e.target.value);
  const handleContractEndDateChange = (e) => setContractEndDate(e.target.value);
  const handleResponsiblePersonChange = (e) => setResponsiblePerson(e.target.value);
  const handleTechTaskChange = (e) => setTechTask(e.target.value);
  const handleInitialInfoChange = (e) => setInitialInfo(e.target.checked);
  const handleAdvancePaymentDateChange = (e) => setAdvancePaymentDate(e.target.value);
  const handleInfoProvidedDateChange = (e) => setInfoProvidedDate(e.target.value);

  const addGoal = () => {
    handleOpenModal();
  };

  const removeGoal = () => {
    if (selectedGoalId && goalsAndTasks.length > 0) {
      const confirmDelete = window.confirm('Удалить выбранную цель работы?');

      if (confirmDelete) {
        const updatedGoals = goalsAndTasks.filter(item => item.id !== selectedGoalId);
        const renumberedGoals = updatedGoals.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        setGoalsAndTasks(renumberedGoals);
        setSelectedGoalId(renumberedGoals.length > 0 ? renumberedGoals[0].id : null);
      }
    } else if (goalsAndTasks.length > 0) {
      const confirmDelete = window.confirm('Удалить последнюю цель работы?');
      if (confirmDelete) {
        const updatedGoals = goalsAndTasks.slice(0, -1);
        const renumberedGoals = updatedGoals.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        setGoalsAndTasks(renumberedGoals);
        setSelectedGoalId(renumberedGoals.length > 0 ? renumberedGoals[renumberedGoals.length - 1].id : null);
      }
    }
  };

  const handleGoalNameChange = (id, value) => {
    setGoalsAndTasks(goalsAndTasks.map(item =>
      item.id === id ? { ...item, name: value } : item
    ));
  };

  const getFormDataForSave = () => {
    return {
      justificationText,
      orgName,
      workTitle,
      workName,
      workCipher,
      contractDate,
      contractNumber,
      contractEndDate,
      responsiblePerson,
      techTask,
      goalsAndTasks,
      initialInfo,
      advancePaymentDate,
      infoProvidedDate
    };
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (onSave) {
        const formData = getFormDataForSave();
        await onSave(formData);
      }
    },
    getFormData: getFormDataForSave
  }));

  const handlePlanOTRClick = () => {
    console.log('План ОТР clicked');
  };

  const handleFillChecklistClick = () => {
    console.log('Заполнить чек-лист clicked');
  };

  return (
    <CContainer fluid className="investigation-justification-tab-container">
      <CForm>
        {/* Обоснование, на основании которого выполняется обследование */}
        <CRow className="mb-3">
          <CCol>
            <CFormLabel className="fw-bold">Обоснование, на основании которого выполняется обследование:</CFormLabel>
            <div className="border-bottom mt-2 mb-3"></div>
          </CCol>
        </CRow>

        {/* Наименование организации */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="orgName">Наименование организации:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <CFormInput
              type="text"
              id="orgName"
              value={orgName}
              onChange={handleOrgNameChange}
            />
          </CCol>
        </CRow>

        {/* Титул работы */}
        <CRow className="mb-3 align-items-start">
          <CCol md={3}>
            <CFormLabel htmlFor="workTitle">Титул работы:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <CFormTextarea
              id="workTitle"
              value={workTitle}
              onChange={handleWorkTitleChange}
              rows={3}
            />
          </CCol>
        </CRow>

        {/* Наименование работы */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="workName">Наименование работы:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <CFormInput
              type="text"
              id="workName"
              value={workName}
              onChange={handleWorkNameChange}
            />
          </CCol>
        </CRow>

        {/* Шифр работы и Дата договора */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="workCipher">Шифр работы:</CFormLabel>
          </CCol>
          <CCol md={3}>
            <CFormInput
              type="text"
              id="workCipher"
              value={workCipher}
              onChange={handleWorkCipherChange}
            />
          </CCol>
          <CCol md={3}>
            <CFormLabel htmlFor="contractDate">Дата договора:</CFormLabel>
          </CCol>
          <CCol md={3}>
            <CFormInput
              type="date"
              id="contractDate"
              value={contractDate}
              onChange={handleContractDateChange}
            />
          </CCol>
        </CRow>

        {/* Номер договора и Дата окончания договора */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="contractNumber">Номер договора:</CFormLabel>
          </CCol>
          <CCol md={3}>
            <CFormInput
              type="text"
              id="contractNumber"
              value={contractNumber}
              onChange={handleContractNumberChange}
            />
          </CCol>
          <CCol md={3}>
            <CFormLabel htmlFor="contractEndDate">Дата окончания договора:</CFormLabel>
          </CCol>
          <CCol md={3}>
            <CFormInput
              type="date"
              id="contractEndDate"
              value={contractEndDate}
              onChange={handleContractEndDateChange}
            />
          </CCol>
        </CRow>

        {/* Ответственный по договору */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="responsiblePerson">Ответственный по договору:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <CFormInput
              type="text"
              id="responsiblePerson"
              value={responsiblePerson}
              onChange={handleResponsiblePersonChange}
            />
          </CCol>
        </CRow>

        {/* Техническое задание и кнопка План ОТР */}
        <CRow className="mb-3 align-items-start">
          <CCol md={3}>
            <CFormLabel htmlFor="techTask">Техническое задание:</CFormLabel>
            <CButton
              color="light"
              variant="outline"
              size="sm"
              className="text-dark border w-100"
              onClick={handlePlanOTRClick}
            >
              План ОТР
            </CButton>
          </CCol>
          <CCol md={1}></CCol>
          <CCol md={8}>
            <CFormTextarea
              id="techTask"
              value={techTask}
              onChange={handleTechTaskChange}
              rows={4}
            />
          </CCol>
        </CRow>

        {/* Цели и задачи работы */}
        <CRow className="mb-3">
          <CCol md={3}>
            <CFormLabel>Цели и задачи работы:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <div className="d-flex gap-2 mb-2">
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border" 
                onClick={addGoal}
              >
                <CIcon icon={cilPlus} className="me-1" />
              </CButton>
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border" 
                onClick={removeGoal}
              >
                <CIcon icon={cilMinus} className="me-1" />
              </CButton>
            </div>
            <CRow>
              <CCol md={8}>
                <CTable bordered small className="mb-0">
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell className="border text-center" style={{ width: '50px' }}>№</CTableHeaderCell>
                      <CTableHeaderCell className="border text-center">Наименование</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {goalsAndTasks.map((item) => (
                      <CTableRow
                        key={item.id}
                        onClick={() => handleGoalClick(item.id)}
                        className={selectedGoalId === item.id ? 'table-primary' : ''}
                        style={{ cursor: 'pointer' }}
                      >
                        <CTableDataCell className="border text-center" style={{ verticalAlign: 'middle' }}>
                          {item.number}
                        </CTableDataCell>
                        <CTableDataCell className="border p-0">
                          <CFormTextarea
                            value={item.name}
                            onChange={(e) => handleGoalNameChange(item.id, e.target.value)}
                            placeholder="Введите наименование"
                            className="border-0 w-100"
                            style={{ 
                              borderRadius: '0', 
                              resize: 'none',
                              minHeight: '38px',
                              fontSize: '0.875rem',
                              wordWrap: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}
                            rows={1}
                            onInput={(e) => {
                              // Автоподстройка высоты
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                          />
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                    {goalsAndTasks.length === 0 && (
                      <CTableRow>
                        <CTableDataCell 
                          colSpan="2" 
                          className="border text-center text-muted py-3"
                        >
                          Нет добавленных целей. Нажмите "+" чтобы добавить.
                        </CTableDataCell>
                      </CTableRow>
                    )}
                  </CTableBody>
                </CTable>
              </CCol>
              <CCol md={4}>
                <div className="d-flex flex-column">
                  <CFormCheck
                    id="initialInfo"
                    label="Исходная информация"
                    checked={initialInfo}
                    onChange={handleInitialInfoChange}
                    className="mb-2"
                  />
                  <CButton
                    color="light"
                    variant="outline"
                    size="sm"
                    className="text-dark border mb-3"
                    onClick={handleFillChecklistClick}
                  >
                    Заполнить чек-лист
                  </CButton>

                  {/* Дата оплаты аванса */}
                  <div className="mb-3">
                    <CFormLabel htmlFor="advancePaymentDate" className="small mb-1">Дата оплаты аванса:</CFormLabel>
                    <CFormInput
                      type="date"
                      id="advancePaymentDate"
                      value={advancePaymentDate}
                      onChange={handleAdvancePaymentDateChange}
                      size="sm"
                    />
                  </div>

                  {/* Дата предоставления всей исходной информации */}
                  <div className="mb-3">
                    <CFormLabel htmlFor="infoProvidedDate" className="small mb-1">Дата предоставления всей исходной информации:</CFormLabel>
                    <CFormInput
                      type="date"
                      id="infoProvidedDate"
                      value={infoProvidedDate}
                      onChange={handleInfoProvidedDateChange}
                      size="sm"
                    />
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </CForm>

      {/* Модальное окно справочника */}
      <CModal visible={showModal} onClose={handleCloseModal} size="lg">
        <CModalHeader>
          <CModalTitle>Справочник: Цели и задачи работы</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {loading ? (
            <div className="text-center">Загрузка данных...</div>
          ) : (
            <>
              <CTable bordered small className="mb-3">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className="border">Наименование</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {goalsData.map((goal, index) => (
                    <CTableRow 
                      key={goal.ID || index}
                      onDoubleClick={() => handleGoalDoubleClick(goal)}
                      onClick={() => setSelectedModalGoalIndex(index)}
                      className={selectedModalGoalIndex === index ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <CTableDataCell 
                        className="border"
                        style={{ 
                          wordWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {goal.Name}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                  {goalsData.length === 0 && !loading && (
                    <CTableRow>
                      <CTableDataCell 
                        colSpan="1" 
                        className="border text-center text-muted py-3"
                      >
                        Нет данных для отображения
                      </CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
              <div className="text-muted small">
                💡 Для добавления: двойной клик по строке
              </div>
            </>
          )}
        </CModalBody>
      </CModal>
    </CContainer>
  );
});

InvestigationJustificationTab.propTypes = {
  initialData: PropTypes.shape({
    justificationText: PropTypes.string,
    orgName: PropTypes.string,
    workTitle: PropTypes.string,
    workName: PropTypes.string,
    workCipher: PropTypes.string,
    contractDate: PropTypes.string,
    contractNumber: PropTypes.string,
    contractEndDate: PropTypes.string,
    responsiblePerson: PropTypes.string,
    techTask: PropTypes.string,
    goalsAndTasks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
      })
    ),
    initialInfo: PropTypes.bool,
    advancePaymentDate: PropTypes.string,
    infoProvidedDate: PropTypes.string
  }),
  onSave: PropTypes.func
};

InvestigationJustificationTab.defaultProps = {
  initialData: null,
  onSave: null
};

export default InvestigationJustificationTab;